import type { IconName } from '../../control/src/components/ui/Icon';

export type ProductKey =
  | 'events'
  | 'calendars'
  | 'attendance'
  | 'people'
  | 'volunteers'
  | 'care'
  | 'communication'
  | 'sites-apps'
  | 'assist';

export type Product = {
  key: ProductKey;
  name: string;
  shortName: string;
  eyebrow: string;
  icon: IconName;
  headline: string;
  summary: string;
  minimumPlan: 'Free' | 'Core' | 'Plus';
  screenshot: string;
  screenshotAlt: string;
  secondaryScreenshot: string;
  secondaryAlt: string;
  secondaryEyebrow: string;
  secondaryHeadline: string;
  secondaryCopy: string;
  features: Array<{ title: string; copy: string }>;
};

export const products: Product[] = [
  {
    key: 'events',
    name: 'Events & Meetings',
    shortName: 'Events',
    eyebrow: 'Plan events',
    icon: 'calendar',
    headline: 'Plan an event and reuse the details.',
    minimumPlan: 'Core',
    summary:
      'Create public events, private gatherings, meetings, registrations, and volunteer needs. Prepare website, email, text, and app-notification drafts without retyping the event.',
    screenshot: '/screenshots/fellora-events.jpg',
    screenshotAlt: 'Fellora Events page showing a weekly church schedule and publishing status',
    secondaryScreenshot: '/screenshots/fellora-public-event.jpg',
    secondaryAlt: 'Published Fellora event page with registration and volunteer choices',
    secondaryEyebrow: 'What people see',
    secondaryHeadline: 'Publish the details your staff reviewed.',
    secondaryCopy:
      'People see the approved time, place, cost, registration, and open volunteer roles. Staff make changes from the Fellora App.',
    features: [
      {
        title: 'Public, private, or limited to selected people',
        copy: 'Create church-wide events, events for selected Groups or Teams, recurring events, and internal meetings.',
      },
      {
        title: 'Registration with the church’s payment page',
        copy: 'Save attendance in Fellora, then send paid registrations to the church’s chosen external payment page.',
      },
      {
        title: 'Keep event details with the event',
        copy: 'Save the schedule, images, volunteer roles, website copy, and message drafts with the event.',
      },
    ],
  },
  {
    key: 'calendars',
    name: 'Calendars',
    shortName: 'Calendars',
    eyebrow: 'Church and personal calendars',
    icon: 'calendar',
    headline: 'Show church events and personal schedules.',
    minimumPlan: 'Free',
    summary:
      'See church events and private care meal schedules on the staff calendar. Give people a public calendar or a personal calendar limited to their events, Groups, Teams, and serving roles.',
    screenshot: '/screenshots/fellora-calendar.jpg',
    screenshotAlt: 'Fellora month calendar with church events, private meal schedules, and subscription options',
    secondaryScreenshot: '/screenshots/fellora-events.jpg',
    secondaryAlt: 'Fellora event schedule showing upcoming church moments',
    secondaryEyebrow: 'Calendar updates',
    secondaryHeadline: 'Change the event once, and subscribed calendars receive the update.',
    secondaryCopy:
      'Public calendar links work with Google Calendar, Apple Calendar, and Outlook. Personal calendar links can be created, replaced, and turned off.',
    features: [
      {
        title: 'Public calendar feeds',
        copy: 'Families can subscribe with the calendar they already use and receive the latest published event details.',
      },
      {
        title: 'Personal calendar links',
        copy: 'A private calendar can include registrations, volunteer assignments, Groups, Teams, and events for selected people.',
      },
      {
        title: 'Care stays private',
        copy: 'Staff can see private meal schedules beside church events without placing care details on the public church calendar.',
      },
    ],
  },
  {
    key: 'attendance',
    name: 'Attendance',
    shortName: 'Attendance',
    eyebrow: 'Church metrics',
    icon: 'trend',
    headline: 'See attendance patterns across services and Areas.',
    minimumPlan: 'Free',
    summary:
      'Record weekly attendance, compare services and Areas, add the measurements your church follows, and see long-term trends without turning Sunday numbers into a spreadsheet project.',
    screenshot: '/screenshots/fellora-attendance.jpg',
    screenshotAlt: 'Fellora Attendance page showing church-wide trends, service filters, ministry measurements, and recent records',
    secondaryScreenshot: '/screenshots/fellora-calendar.jpg',
    secondaryAlt: 'Fellora Calendar showing recurring services organized by church Area',
    secondaryEyebrow: 'Services and Areas',
    secondaryHeadline: 'Keep each service separate and still understand the church-wide total.',
    secondaryCopy:
      'Connect attendance to the same Areas and recurring services used on the Calendar. Compare one service, one Area, a ministry measurement, or the whole church over time.',
    features: [
      {
        title: 'Multiple services and Areas',
        copy: 'Record separate service counts even when several services meet in the same room, building, or ministry Area.',
      },
      {
        title: 'Measurements that fit your church',
        copy: 'Track worship, children, youth, volunteers, first-time guests, baptisms, decisions, or another measurement you create.',
      },
      {
        title: 'Trends, notes, and protected corrections',
        copy: 'Review past Sundays and preserve context with notes. Only owners can change a completed record, with clear warnings and an edit history.',
      },
    ],
  },
  {
    key: 'people',
    name: 'People, Groups & Teams',
    shortName: 'People',
    eyebrow: 'Church directory and households',
    icon: 'people',
    headline: 'Manage your directory, households, Groups, and Teams.',
    minimumPlan: 'Free',
    summary:
      'Manage profiles, households, staff, Groups, Teams, church-app access, activity, and messaging choices. A directory record never counts as permission to send messages.',
    screenshot: '/screenshots/fellora-people.jpg',
    screenshotAlt: 'Fellora People page with church app use, self-service QR signup, and directory',
    secondaryScreenshot: '/screenshots/fellora-care-tables.jpg',
    secondaryAlt: 'Private Fellora Care meal plan connected to saved people',
    secondaryEyebrow: 'People choose their messages',
    secondaryHeadline: 'Belonging, serving, and receiving care do not create message permission.',
    secondaryCopy:
      'Fellora keeps profiles, church involvement, app access, and message permission separate so staff do not confuse one with another.',
    features: [
      {
        title: 'Directory and self-service signup',
        copy: 'Add people directly or let them create a profile through a church link or QR code.',
      },
      {
        title: 'Groups and Teams',
        copy: 'Organize ministries, classes, committees, staff, and Team conversations without turning membership into consent.',
      },
      {
        title: 'Church app access',
        copy: 'Invite, enable, or revoke member access while preserving participation history and authorized church records.',
      },
    ],
  },
  {
    key: 'volunteers',
    name: 'Volunteers',
    shortName: 'Volunteers',
    eyebrow: 'Volunteer teams and event roles',
    icon: 'hand',
    headline: 'Set up volunteer teams and fill event roles.',
    minimumPlan: 'Core',
    summary:
      'Create volunteer groups with an icon and leader, reuse them on an event, or add a role that is only needed once.',
    screenshot: '/screenshots/fellora-volunteers.jpg',
    screenshotAlt: 'Fellora volunteer page with reusable group choices, event roles, capacity, and assignments',
    secondaryScreenshot: '/screenshots/fellora-public-event.jpg',
    secondaryAlt: 'Public Fellora event page where a person can choose an open volunteer role',
    secondaryEyebrow: 'Serving at an event',
    secondaryHeadline: 'People can see the need; leaders can see the event roster.',
    secondaryCopy:
      'A volunteer group does not create another permanent people list. It brings the group identity and leader to the event, where assignments stay specific.',
    features: [
      {
        title: 'Volunteer groups that fit your church',
        copy: 'Staff choose the names, icons, descriptions, and leaders instead of starting from a preset ministry list.',
      },
      {
        title: 'One-time roles when needed',
        copy: 'Add an event-only role, capacity, instructions, and custom or AI-assisted icon without changing the reusable library.',
      },
      {
        title: 'Event-specific leadership',
        copy: 'Leaders receive the volunteers attached to their role for that event and can see who they are responsible for.',
      },
    ],
  },
  {
    key: 'care',
    name: 'Care',
    shortName: 'Care',
    eyebrow: 'Meals',
    icon: 'pie',
    headline: 'Organize meals for someone who needs support.',
    minimumPlan: 'Core',
    summary:
      'Create a private meal schedule with recipient details, volunteer instructions, staff notes, delivery dates, and messaging choices kept separate.',
    screenshot: '/screenshots/fellora-care-tables.jpg',
    screenshotAlt: 'Private Fellora Care meal plan with recipients, delivery dates, reminders, and volunteers',
    secondaryScreenshot: '/screenshots/fellora-calendar.jpg',
    secondaryAlt: 'Fellora calendar showing private meal plans beside church events',
    secondaryEyebrow: 'Keep care private',
    secondaryHeadline: 'Care belongs on the staff calendar—not the public one.',
    secondaryCopy:
      'Authorized staff can coordinate the full plan. Recipients and volunteers receive only the appropriate private view and details.',
    features: [
      {
        title: 'Recipient and volunteer privacy',
        copy: 'Keep internal notes separate from recipient details and volunteer delivery instructions.',
      },
      {
        title: 'Delivery dates and openings',
        copy: 'Assign dates, leave openings for volunteers, track menu details, and send private links for each delivery.',
      },
      {
        title: 'Groups, people, and messaging choices',
        copy: 'Attach an existing Group or Team, add someone directly, and check current email or text permission before contact.',
      },
    ],
  },
  {
    key: 'communication',
    name: 'Communication',
    shortName: 'Communication',
    eyebrow: 'Church messages',
    icon: 'message',
    headline: 'Build one campaign for every channel you need.',
    minimumPlan: 'Plus',
    summary:
      'Start with a ready-made template, write it yourself, or ask Fellora AI for help. Review the email, text, and app notification together, then send now or schedule the campaign.',
    screenshot: '/screenshots/fellora-messages.jpg',
    screenshotAlt: 'Fellora Messages page with a guided campaign builder, templates, recent campaigns, and Stories',
    secondaryScreenshot: '/screenshots/fellora-distribution.jpg',
    secondaryAlt: 'Fellora event sharing page with website, calendar, email, text, and app notification choices',
    secondaryEyebrow: 'Campaigns and Stories',
    secondaryHeadline: 'Send one update now—or welcome people automatically when they join.',
    secondaryCopy:
      'Keep each email, text, and app notification together as one campaign. Stories can welcome new visitors or follow up when someone joins a Group or Team.',
    features: [
      {
        title: 'A guided campaign builder',
        copy: 'Choose the audience and channels, use AI or write it yourself, review every version, and schedule only when it is ready.',
      },
      {
        title: 'Email, text, and app notification designers',
        copy: 'Give email the space it needs for images and formatting while keeping text and app notifications clear and concise.',
      },
      {
        title: 'Preference and consent aware',
        copy: 'Use each person’s chosen channel or select specific channels without treating directory, Group, Team, or event membership as consent.',
      },
    ],
  },
  {
    key: 'sites-apps',
    name: 'Sites & Apps',
    shortName: 'Sites & Apps',
    eyebrow: 'Staff app and church app',
    icon: 'website',
    headline: 'Give staff and church members the right app.',
    minimumPlan: 'Core',
    summary:
      'Build and publish your church website, manage church work in Fellora, and give members and volunteers an installable church app with events, serving, messages, and notifications.',
    screenshot: '/screenshots/fellora-designer.jpg',
    screenshotAlt: 'Fellora Designer showing church website sections, style controls, and a live preview',
    secondaryScreenshot: '/screenshots/fellora-church-site.jpg',
    secondaryAlt: 'Published church website created in Fellora with a welcome hero and church navigation',
    secondaryEyebrow: 'From editor to published site',
    secondaryHeadline: 'Publish your church’s welcome, service times, and upcoming events.',
    secondaryCopy:
      'Use the visual editor to publish contact details, upcoming events, and the link to your church app.',
    features: [
      {
        title: 'Fellora App for staff',
        copy: 'Manage events, calendars, people, volunteers, care, messages, website design, and settings.',
      },
      {
        title: 'Church app for members and volunteers',
        copy: 'A church-branded app people can add to their phone for events, serving, messages, calendars, profile settings, and notifications.',
      },
      {
        title: 'Church website Designer',
        copy: 'Edit sections, colors, headings, images, service times, and event content while seeing the page take shape.',
      },
    ],
  },
  {
    key: 'assist',
    name: 'Fellora Assist',
    shortName: 'Fellora Assist',
    eyebrow: 'AI drafting with staff review',
    icon: 'sparkles',
    headline: 'Get a first draft, then review every part.',
    minimumPlan: 'Core',
    summary:
      'Fellora AI can prepare event copy, message drafts, images, and volunteer ideas. Support answers use published manager guides and your church’s approved information without publishing changes on their own.',
    screenshot: '/screenshots/fellora-assist.jpg',
    screenshotAlt: 'Fellora AI event planner with editable questions, generated channel drafts, and approval controls',
    secondaryScreenshot: '/screenshots/fellora-volunteers.jpg',
    secondaryAlt: 'Fellora volunteer page with AI-assisted icon controls and event roles',
    secondaryEyebrow: 'Staff approval required',
    secondaryHeadline: 'AI drafts stay unpublished until a person approves them.',
    secondaryCopy:
      'Staff can edit, ask for changes, approve, or hold each piece. Support answers can show which published guides they used.',
    features: [
      {
        title: 'Complete event starting points',
        copy: 'Answer a short brief and receive a reviewable event page, email, text, app notification, banner, and volunteer suggestions.',
      },
      {
        title: 'Human approval at every step',
        copy: 'Edit or hold individual pieces, request a revision, and publish only after an authorized person is satisfied.',
      },
      {
        title: 'Published manager knowledge',
        copy: 'Church-facing AI help uses published Fellora App guides—not internal Ops-only runbooks—and can show the sources behind an answer.',
      },
    ],
  },
];

export const productByKey = new Map(products.map((product) => [product.key, product]));
